// src/pages/components/FourPartyPricingModal.jsx
import React from "react";
import { X, Calculator, User, Store, UserCheck, Landmark } from "lucide-react";
import { calculateFourPartyPricing } from "../utils/calculationutil";

const FourPartyPricingModal = ({ order, rtgsSaleRate = 0, onClose }) => {
  if (!order) return null;

  const pricing = calculateFourPartyPricing(order, rtgsSaleRate);
  const { buyer, seller, operator, jmi, weights } = pricing;

  const inr = (n) =>
    `₹${Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const round3 = (n) => Number(n || 0).toFixed(3);
  const round2 = (n) => Number(n || 0).toFixed(2);

  return (
    <div className="pricing-overlay" onClick={onClose}>
      <div className="pricing-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="pricing-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div className="pricing-icon-wrap">
              <Calculator size={20} color="#4f46e5" />
            </div>
            <div>
              <h3 className="pricing-title">
                4-Party Pricing Breakdown — {order.productName || order.id || "Order"}
              </h3>
              <p className="pricing-subtitle">
                Order ID: #{order.id || order.docId} · Seller: {order.sellerId || "—"} · Buyer: {order.buyerId || "—"}
              </p>
            </div>
          </div>
          <button className="pricing-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Weights & Live Rate Bar */}
        <div className="pricing-info-bar">
          <div>
            <span>Net Wt: <strong>{round3(weights.netWt)}g</strong></span>
            <span className="pricing-sep">·</span>
            <span>Gross Wt: <strong>{round3(weights.grossWt)}g</strong></span>
            <span className="pricing-sep">·</span>
            <span>Spec Wt: <strong>{round3(weights.specWt)}g</strong></span>
            <span className="pricing-sep">·</span>
            <span>Live RTGS Sale Rate: <strong>{inr(rtgsSaleRate)}/g</strong></span>
          </div>
        </div>

        {/* 4 Pricing Cards Grid */}
        <div className="pricing-body">
          <div className="pricing-grid">
            {/* 1. Buyer Pricing */}
            <div className="party-card party-card--buyer">
              <div className="party-card-header">
                <User size={18} />
                <h4>1. Buyer Pricing</h4>
              </div>
              <div className="party-card-content">
                <div className="party-row">
                  <span>Fine Percentage</span>
                  <strong>{round2(buyer.finePercentage)}%</strong>
                </div>
                <div className="party-row">
                  <span>Fine Weight</span>
                  <strong>{round3(buyer.fineWeight)}g</strong>
                </div>
                <div className="party-row">
                  <span>Set Making Charge</span>
                  <span>{inr(buyer.setMc)}</span>
                </div>
                <div className="party-row">
                  <span>Gram MC ({round3(weights.netWt)}g)</span>
                  <span>{inr(buyer.gramMc)}</span>
                </div>
                <div className="party-row">
                  <span>Spec MC ({round3(weights.specWt)}g)</span>
                  <span>{inr(buyer.specMc)}</span>
                </div>
                <div className="party-row party-row--total">
                  <span>Total Buyer Amount (MC)</span>
                  <strong>{inr(buyer.totalAmount)}</strong>
                </div>
              </div>
            </div>

            {/* 2. Seller Pricing */}
            <div className="party-card party-card--seller">
              <div className="party-card-header">
                <Store size={18} />
                <h4>2. Seller Pricing</h4>
              </div>
              <div className="party-card-content">
                <div className="party-row">
                  <span>Fine Percentage</span>
                  <strong>{round2(seller.finePercentage)}%</strong>
                </div>
                <div className="party-row">
                  <span>Fine Weight</span>
                  <strong>{round3(seller.fineWeight)}g</strong>
                </div>
                <div className="party-row">
                  <span>Set Making Charge</span>
                  <span>{inr(seller.setMc)}</span>
                </div>
                <div className="party-row">
                  <span>Gram MC ({round3(weights.netWt)}g)</span>
                  <span>{inr(seller.gramMc)}</span>
                </div>
                <div className="party-row">
                  <span>Spec MC ({round3(weights.specWt)}g)</span>
                  <span>{inr(seller.specMc)}</span>
                </div>
                <div className="party-row party-row--total">
                  <span>Total Seller Amount (MC)</span>
                  <strong>{inr(seller.totalAmount)}</strong>
                </div>
              </div>
            </div>

            {/* 3. Operator Pricing */}
            <div className="party-card party-card--operator">
              <div className="party-card-header">
                <UserCheck size={18} />
                <h4>3. Operator Pricing</h4>
              </div>
              <div className="party-card-content">
                <div className="party-row">
                  <span>Fine Percentage</span>
                  <strong>{round2(operator.finePercentage)}%</strong>
                </div>
                <div className="party-row">
                  <span>Fine Weight</span>
                  <strong>{round3(operator.fineWeight)}g</strong>
                </div>
                <div className="party-row">
                  <span>Set Making Charge</span>
                  <span>{inr(operator.setMc)}</span>
                </div>
                <div className="party-row">
                  <span>Gram MC ({round3(weights.netWt)}g)</span>
                  <span>{inr(operator.gramMc)}</span>
                </div>
                <div className="party-row">
                  <span>Spec MC ({round3(weights.specWt)}g)</span>
                  <span>{inr(operator.specMc)}</span>
                </div>
                <div className="party-row party-row--total">
                  <span>Total Operator Amount (MC)</span>
                  <strong>{inr(operator.totalAmount)}</strong>
                </div>
              </div>
            </div>

            {/* 4. JMI Pricing */}
            <div className="party-card party-card--jmi">
              <div className="party-card-header">
                <Landmark size={18} />
                <h4>4. JMI Pricing (Commission)</h4>
              </div>
              <div className="party-card-content">
                <div className="party-row">
                  <span>JMI Fine %</span>
                  <strong>{round2(jmi.finePercentage)}%</strong>
                </div>
                <div className="party-row">
                  <span>JMI Fine Weight</span>
                  <strong style={{ color: "#d97706" }}>{round3(jmi.fineWeight)}g</strong>
                </div>
                <div className="party-row">
                  <span>Fine Valuation (Fine × Rate)</span>
                  <strong style={{ color: "#059669" }}>{inr(jmi.fineAmount)}</strong>
                </div>
                <div className="party-row">
                  <span>JMI Making Charges (MC)</span>
                  <span>{inr(jmi.totalMc)}</span>
                </div>
                <div className="party-row party-row--total party-row--jmi-highlight">
                  <span>Total JMI Commission (Fine + MC)</span>
                  <strong style={{ color: "#4338ca", fontSize: "15px" }}>
                    {inr(jmi.totalCommission)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pricing-footer">
          <button className="pricing-btn-close" onClick={onClose}>
            Close Breakdown
          </button>
        </div>
      </div>

      <style>{`
        .pricing-overlay {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1200; padding: 16px;
        }
        .pricing-modal {
          background: #ffffff; border-radius: 14px;
          width: 100%; max-width: 900px; max-height: 90vh;
          display: flex; flex-direction: column;
          box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.3);
          overflow: hidden; border: 1px solid #e2e8f0;
        }
        .pricing-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px; border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }
        .pricing-icon-wrap {
          width: 36px; height: 36px; border-radius: 8px;
          background: #eef2ff; display: flex; align-items: center; justify-content: center;
        }
        .pricing-title { margin: 0; font-size: 17px; font-weight: 700; color: #1e293b; }
        .pricing-subtitle { margin: 2px 0 0; font-size: 12px; color: #64748b; }
        .pricing-close {
          background: none; border: none; cursor: pointer; color: #94a3b8;
          padding: 6px; border-radius: 6px; display: flex; align-items: center;
        }
        .pricing-close:hover { background: #f1f5f9; color: #1e293b; }
        .pricing-info-bar {
          padding: 10px 20px; background: #e0e7ff; border-bottom: 1px solid #c7d2fe;
          font-size: 13px; color: #3730a3; display: flex; align-items: center;
        }
        .pricing-sep { margin: 0 8px; color: #a5b4fc; }
        .pricing-body { padding: 20px; overflow-y: auto; }
        .pricing-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }
        .party-card {
          border-radius: 10px; border: 1.5px solid #e2e8f0;
          background: #fafafa; overflow: hidden; display: flex; flex-direction: column;
        }
        .party-card-header {
          display: flex; align-items: center; gap: 8px;
          padding: 10px 14px; font-size: 13px; font-weight: 700;
        }
        .party-card-header h4 { margin: 0; font-size: 13px; font-weight: 700; }
        .party-card--buyer .party-card-header { background: #dbeafe; color: #1e40af; border-bottom: 1px solid #bfdbfe; }
        .party-card--seller .party-card-header { background: #fef3c7; color: #92400e; border-bottom: 1px solid #fde68a; }
        .party-card--operator .party-card-header { background: #dcfce7; color: #166534; border-bottom: 1px solid #bbf7d0; }
        .party-card--jmi .party-card-header { background: #ede9fe; color: #5b21b6; border-bottom: 1px solid #ddd6fe; }
        
        .party-card-content {
          padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; font-size: 12px;
        }
        .party-row {
          display: flex; justify-content: space-between; align-items: center; color: #475569;
        }
        .party-row strong { color: #1e293b; }
        .party-row--total {
          border-top: 1px dashed #cbd5e1; padding-top: 8px; margin-top: 4px;
          font-weight: 700; color: #1e293b;
        }
        .party-row--jmi-highlight {
          background: #f5f3ff; margin: 4px -14px -12px; padding: 10px 14px;
          border-top: 1px solid #c4b5fd; border-radius: 0 0 8px 8px;
        }
        .pricing-footer {
          padding: 14px 20px; border-top: 1px solid #e2e8f0; background: #f8fafc;
          display: flex; justify-content: flex-end;
        }
        .pricing-btn-close {
          padding: 8px 16px; border: 1px solid #cbd5e1; border-radius: 8px;
          background: #ffffff; color: #475569; font-weight: 600; font-size: 13px; cursor: pointer;
        }
        .pricing-btn-close:hover { background: #f1f5f9; }
      `}</style>
    </div>
  );
};

export default FourPartyPricingModal;
